/**
 * Чистая логика «Ленты визуалов»: откуда кадр, чем отличается от исходного
 * набора, как называется строка изменений. Без React — чтобы её можно было
 * проверять таблицей.
 */

export type RibbonOrigin = 'funnel' | 'series' | 'manual' | 'mp_import'

export interface VisualMeta {
  origin: RibbonOrigin | null
  /** Роль кадра. Есть только там, где она надёжно читается из адреса. */
  role: string | null
  /** Номер слайда серии (`fs_slide_N`). */
  slide: number | null
}

export const ORIGIN_LABEL: Record<RibbonOrigin, string> = {
  funnel: 'Воронка',
  series: 'Воронка серий',
  manual: 'Ручная загрузка',
  mp_import: 'С площадки',
}

const SERIES_SLIDE = /^fs_slide_(\d+)$/i

const pathSegments = (url: string): string[] => {
  const raw = String(url || '').trim()
  if (!raw) return []
  let path = raw
  if (raw.includes('?path=')) {
    const tail = raw.split('?path=')[1] ?? ''
    try {
      path = decodeURIComponent(tail)
    } catch {
      path = tail
    }
  } else if (/^https?:\/\//i.test(raw)) {
    try {
      path = decodeURIComponent(new URL(raw).pathname)
    } catch {
      return []
    }
  } else {
    path = raw.split('?')[0]
  }
  return path.split(/[\\/]/).filter(Boolean)
}

/** Имя файла из адреса — для подписи кадра и имени скачиваемого файла. */
export const fileNameOf = (url: string): string => {
  const segments = pathSegments(url)
  return segments[segments.length - 1] || String(url || '')
}

/**
 * Происхождение и роль кадра по адресу.
 *
 * Читается только то, что в адресе есть буквально: каталог `manual/` — ручная
 * загрузка, `mp_import/` — перенос с витрины, `fs_slide_N/` — слайд серии
 * контент-воронки. Всё остальное — «воронка» без роли: какому элементу воронки
 * соответствует файл, адрес не говорит, а выдумывать название слайда нельзя.
 * Адрес без распознаваемого дерева (прямая ссылка, `?path=` из черновика) —
 * `origin: null`, подпись не показывается.
 */
export function deriveVisualMeta(url: string): VisualMeta {
  const segments = pathSegments(url)
  if (segments.length < 2) return { origin: null, role: null, slide: null }
  const folders = segments.slice(0, -1)
  const series = folders.map((part) => SERIES_SLIDE.exec(part)).find(Boolean)
  if (series) {
    const slide = Number(series[1])
    return { origin: 'series', role: `Слайд ${slide}`, slide }
  }
  if (folders.includes('manual')) return { origin: 'manual', role: null, slide: null }
  if (folders.includes('mp_import')) return { origin: 'mp_import', role: null, slide: null }
  return { origin: null, role: null, slide: null }
}

export type SlotMark = 'new' | 'moved' | 'replaced'

export const MARK_LABEL: Record<SlotMark, string> = {
  new: 'новое',
  moved: 'перемещено',
  replaced: 'заменено',
}

export interface RibbonDiff {
  /** Отметка по индексу текущего набора. */
  marks: Map<number, SlotMark>
  /** Подписи строки изменений: «порядок», «+1», «заменено 1», «−1». */
  summary: string[]
  changed: boolean
  added: number
  removed: number
  replaced: number
  reordered: boolean
}

/** Номера элементов самой длинной возрастающей цепочки (индексы в `values`). */
function longestIncreasingSubsequence(values: number[]): Set<number> {
  const n = values.length
  if (n === 0) return new Set()
  const length = new Array<number>(n).fill(1)
  const previous = new Array<number>(n).fill(-1)
  let best = 0
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < i; j += 1) {
      if (values[j] < values[i] && length[j] + 1 > length[i]) {
        length[i] = length[j] + 1
        previous[i] = j
      }
    }
    if (length[i] > length[best]) best = i
  }
  const keep = new Set<number>()
  for (let at = best; at !== -1; at = previous[at]) keep.add(at)
  return keep
}

/**
 * Чем текущий набор отличается от исходного.
 *
 * «Перемещено» отмечает только сдвинутые кадры, а не весь хвост после вставки
 * или удаления: оставшиеся на своих местах кадры образуют самую длинную
 * возрастающую цепочку исходных позиций, остальные — переставлены.
 * `replaced` — карта «новый адрес → прежний», её ведёт сама лента, потому что
 * по двум спискам адресов замену от «убрал один, добавил другой» не отличить.
 */
export function diffRibbon(
  baseline: readonly string[] | undefined,
  current: readonly string[],
  replaced: ReadonlyMap<string, string> = new Map(),
): RibbonDiff {
  const empty: RibbonDiff = {
    marks: new Map(), summary: [], changed: false, added: 0, removed: 0, replaced: 0, reordered: false,
  }
  if (!baseline) return empty

  const baselineIndex = new Map<string, number>()
  baseline.forEach((url, index) => {
    if (!baselineIndex.has(url)) baselineIndex.set(url, index)
  })

  const marks = new Map<number, SlotMark>()
  const replacedOriginals = new Set<string>()
  const keptPositions: number[] = []
  const keptSlots: number[] = []

  current.forEach((url, slot) => {
    const previous = replaced.get(url)
    if (previous !== undefined && baselineIndex.has(previous) && !baselineIndex.has(url)) {
      marks.set(slot, 'replaced')
      replacedOriginals.add(previous)
      return
    }
    const at = baselineIndex.get(url)
    if (at === undefined) {
      marks.set(slot, 'new')
      return
    }
    keptPositions.push(at)
    keptSlots.push(slot)
  })

  const stable = longestIncreasingSubsequence(keptPositions)
  keptSlots.forEach((slot, order) => {
    if (!stable.has(order)) marks.set(slot, 'moved')
  })

  const currentSet = new Set(current)
  const removed = baseline.filter((url) => !currentSet.has(url) && !replacedOriginals.has(url)).length
  const added = [...marks.values()].filter((mark) => mark === 'new').length
  const replacedCount = [...marks.values()].filter((mark) => mark === 'replaced').length
  const reordered = [...marks.values()].some((mark) => mark === 'moved')

  const summary: string[] = []
  if (reordered) summary.push('порядок')
  if (added) summary.push(`+${added}`)
  if (replacedCount) summary.push(`заменено ${replacedCount}`)
  if (removed) summary.push(`−${removed}`)

  return {
    marks, summary, changed: summary.length > 0, added, removed, replaced: replacedCount, reordered,
  }
}

/** «1 карточку», «2 карточки», «5 карточек» — для фразы «затронет …». */
export function affectedCardsText(count: number): string {
  const n = Math.abs(Math.trunc(count))
  const last = n % 10
  const tail = n % 100
  if (tail >= 11 && tail <= 14) return `${n} карточек`
  if (last === 1) return `${n} карточку`
  if (last >= 2 && last <= 4) return `${n} карточки`
  return `${n} карточек`
}

export type RibbonAspect = '3:4' | '1:1'

export const ASPECT_CLASS: Record<RibbonAspect, string> = { '3:4': 'aspect-[3/4]', '1:1': 'aspect-square' }

export type CompareState = 'same' | 'shifted' | 'absent'

/** Где кадр витрины находится в текущем наборе. */
export function compareRemote(
  remote: readonly string[],
  current: readonly string[],
): Array<{ url: string; state: CompareState; at: number | null }> {
  return remote.map((url, index) => {
    const at = current.indexOf(url)
    if (at === -1) return { url, state: 'absent' as const, at: null }
    return { url, state: at === index ? ('same' as const) : ('shifted' as const), at: at + 1 }
  })
}
